import React, { useMemo } from "react";

import CommonModal from "../../../Components/Modal";
import { Executive } from "../../../Store/Reducers/OpsSlice";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../../Utils/Helper/accountTierHelpers";
import { getInitials } from "../../../Utils/Helper/Helper";

interface AssignedAccountsModalProps {
  isOpen: boolean;
  toggle: () => void;
  executive: Executive;
}

// Map assignment_type from API to human readable label
const getAssignmentLabel = (assignmentType?: string | null) => {
  if (!assignmentType) return "Sponsor";

  const type = assignmentType.toLowerCase();
  if (type === "primary") return "Primary Sponsor";
  if (type === "secondary") return "Secondary Sponsor";

  return `${assignmentType} Sponsor`;
};

const AssignedAccountsModal: React.FC<AssignedAccountsModalProps> = ({
  isOpen,
  toggle,
  executive,
}) => {
  const { formatTier } = useAccountTiers();
  const sortedAccounts = useMemo(
    () =>
      executive.accounts && executive.accounts.length > 0
        ? [...executive.accounts].sort(
            (a, b) =>
              new Date(b.assigned_on).getTime() -
              new Date(a.assigned_on).getTime()
          )
        : [],
    [executive.accounts]
  );

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      showHeader={false}
      size="md"
      className="p-0"
    >
      <div className="w-full">
        {/* Custom purple header */}
        <div className="-mx-4 -mt-4 mb-4 rounded-t-lg bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-white">
                Assigned Accounts
              </div>
              <div className="mt-0.5 text-xs text-purple-100 truncate">
                {executive.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            className="text-white/80 hover:text-white text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {sortedAccounts.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            No accounts assigned to this executive yet.
          </div>
        ) : (
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {sortedAccounts.map((account, idx) => (
              <div
                key={`${account.name}-${idx}`}
                className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-700 text-xs font-semibold flex-shrink-0">
                    {getInitials(account.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">
                      {account.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getAssignmentLabel(account.assignment_type)}
                    </div>
                  </div>
                </div>
                <span
                  className={`ml-3 inline-flex items-center justify-center rounded-md px-2.5 py-1 text-xs font-semibold ${getTierBadgeClasses(
                    account.tier
                  )}`}
                >
                  {formatTier(account.tier) ||
                    account.tier?.toUpperCase() ||
                    "N/A"}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={toggle}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </CommonModal>
  );
};

export default AssignedAccountsModal;
